import { FormInput } from "components/basic/FormInput";
import { FormSelect } from "components/basic/FormSelect";
import { ButtonRound } from "components/basic/Buttons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchedAddressByPostcode } from "./store/addressSlice";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
type Props = {
  handleChange: (e: React.FormEvent<any>) => void;
  setFieldValue: Function;
  handleBlur: (e: React.FocusEvent<any, Element>) => void;
  values?: any;
  errors?: any;
  touched?: any;
  onSelectChange?: (option: any) => void;
  selectErrorMsg?: string;
  selectActionType?: "setInputFields" | "select" | "custom";
  newAddressView?: boolean;
};

const isValidField = (
  values: any,
  touched: any,
  errors: any,
  field: string
): boolean => {
  return !!touched?.[field] && !errors?.[field] && !!values?.[field];
};

export const Address = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  onSelectChange,
  selectErrorMsg = "",
  selectActionType = "setInputFields",
  newAddressView = false,
}: Props) => {
  const dispatch = useAppDispatch();

  const [isAddressLocked, setIsAddressLocked] = useState(false);
  const hasInitializedLock = useRef(false);

  useEffect(() => {
    if (!hasInitializedLock.current && values?.isManualAddress) {
      setIsAddressLocked(true);
      hasInitializedLock.current = true;
    }
  }, [values?.isManualAddress]);

  const isManualAddress = useMemo(() => {
    return !!values?.isManualAddress;
  }, [values]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        !isManualAddress &&
        values.postcode &&
        values.postcode.length > 1 &&
        !errors.postcode
      ) {
        dispatch(fetchedAddressByPostcode(values.postcode));
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [values.postcode, errors.postcode, isManualAddress]);

  const addresses = useAppSelector((state) => state.address.addresses);

  const addressOptions = useMemo(() => {
    return addresses.map((address) => address.label);
  }, [addresses]);

  const handleAddressSelectChange = useCallback(
    (e: React.FormEvent<HTMLSelectElement>) => {
      const value = e.currentTarget.value;
      const option = addresses.find((address) => address.label === value);

      switch (selectActionType) {
        case "setInputFields": {
          if (option) {
            const { info } = option;

            setFieldValue("address1", info.line1 || "", true);
            setFieldValue("address2", info.line2 || "", true);
            setFieldValue("address3", info.town || "", true);
            setFieldValue("address4", info.county || "", true);

            if (newAddressView) {
              const line1Parts = (info.line1 || "").split(" ");
              const buildingNumber = line1Parts.length > 0 && !isNaN(Number(line1Parts[0]))
                ? line1Parts[0]
                : "";
              const street = line1Parts.length > 0
                ? line1Parts.slice(buildingNumber ? 1 : 0).join(" ")
                : info.line1 || "";

              setFieldValue("buildingNumber", buildingNumber || "", true);
              setFieldValue("street", street || "", true);
              setFieldValue("buildingName", "", true);
              setFieldValue("flatNumber", "", true);
              setFieldValue("locality", info.line2 || "", true);
              setFieldValue("townOrCity", info.town || "", true);
              setFieldValue("county", info.county || "", true);
            }

            setTimeout(() => {
              setFieldValue("isManualAddress", true);
              setIsAddressLocked(true);
            }, 0);
          }
          break;
        }
        case "select": {
          handleChange(e);
          break;
        }
        default: {
          break;
        }
      }
      if (onSelectChange) {
        onSelectChange(option);
      }
    },
    [selectActionType, onSelectChange, addresses, setFieldValue, handleChange, newAddressView]
  );

  useEffect(() => {
    if (newAddressView && values?.isManualAddress) {
      let address1 = '';
      if (values.flatNumber) {
        address1 += values.flatNumber + ' ';
      }
      if (values.buildingName) {
        address1 += values.buildingName + ' ';
      }
      if (values.buildingNumber) {
        address1 += values.buildingNumber + ' ';
      }
      if (values.street) {
        address1 += values.street;
      }

      if (address1.trim()) {
        setFieldValue("address1", address1.trim(), false);
      }

      if (values.locality) {
        setFieldValue("address2", values.locality, false);
      }

      if (values.townOrCity) {
        setFieldValue("address3", values.townOrCity, false);
      }

      if (values.county) {
        setFieldValue("address4", values.county, false);
      }
    }
  }, [
    newAddressView,
    values?.isManualAddress,
    values?.flatNumber,
    values?.buildingName,
    values?.buildingNumber,
    values?.street,
    values?.locality,
    values?.townOrCity,
    values?.county,
    setFieldValue
  ]);

  return (
    <>
      {!isManualAddress ? (
        <div className="grid gap-4 md:grid-cols-3">
          <FormInput
            wrapperClassName="md:col-span-1"
            label="Postcode"
            placeholder="Postcode"
            name="postcode"
            autocomplete="postal-code"
            value={values?.postcode}
            errorMsg={touched.postcode && errors?.postcode}
            valid={isValidField(values, touched, errors, "postcode")}
            onBlur={handleBlur}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setFieldValue("postcode", e.currentTarget.value?.toUpperCase());
            }}
          />
          <FormSelect
            wrapperClassName="md:col-span-2"
            label="Address"
            options={addressOptions}
            disabled={addresses.length === 0}
            value={values.address}
            onChange={handleAddressSelectChange}
            errorMsg={selectErrorMsg}
          />
        </div>
      ) : (
        <>
          <FormInput
            label="Postcode"
            placeholder="Postcode"
            name="postcode"
            autocomplete="postal-code"
            value={values?.postcode}
            errorMsg={touched.postcode && errors?.postcode}
            valid={isValidField(values, touched, errors, "postcode")}
            onBlur={handleBlur}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setFieldValue("postcode", e.currentTarget.value?.toUpperCase());
            }}
          />
        </>
      )}
      {isManualAddress && (
        <>
          <FormInput
            name="company"
            autocomplete="organization"
            value={values?.company}
            errorMsg={touched.company && errors?.company}
            valid={isValidField(values, touched, errors, "company")}
            onBlur={handleBlur}
            onChange={handleChange}
            label="Company Name (If applicable)"
            placeholder="Company Name (If applicable)"
            disabled={isAddressLocked}
          />

          {newAddressView ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  name="flatNumber"
                  value={values?.flatNumber}
                  errorMsg={touched.flatNumber && errors?.flatNumber}
                  valid={isValidField(values, touched, errors, "flatNumber")}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  label="Flat Number"
                  placeholder="Flat Number"
                  disabled={isAddressLocked}
                />
                <FormInput
                  name="buildingNumber"
                  value={values?.buildingNumber}
                  errorMsg={touched.buildingNumber && errors?.buildingNumber}
                  valid={isValidField(values, touched, errors, "buildingNumber")}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  label="Building Number"
                  placeholder="Building Number"
                  disabled={isAddressLocked}
                />
              </div>

              <FormInput
                name="buildingName"
                value={values?.buildingName}
                errorMsg={touched.buildingName && errors?.buildingName}
                valid={isValidField(values, touched, errors, "buildingName")}
                onBlur={handleBlur}
                onChange={handleChange}
                label="Building Name"
                placeholder="Building Name"
                disabled={isAddressLocked}
              />

              <FormInput
                name="street"
                autocomplete="street-address"
                value={values?.street}
                errorMsg={touched.street && errors?.street}
                valid={isValidField(values, touched, errors, "street")}
                onBlur={handleBlur}
                onChange={handleChange}
                label="Street"
                placeholder="Street"
                disabled={isAddressLocked}
              />

              <FormInput
                name="locality"
                value={values?.locality}
                errorMsg={touched.locality && errors?.locality}
                valid={isValidField(values, touched, errors, "locality")}
                onBlur={handleBlur}
                onChange={handleChange}
                label="Locality"
                placeholder="Locality"
                disabled={isAddressLocked}
              />

              <FormInput
                name="townOrCity"
                autocomplete="address-level2"
                value={values?.townOrCity}
                errorMsg={touched.townOrCity && errors?.townOrCity}
                valid={isValidField(values, touched, errors, "townOrCity")}
                onBlur={handleBlur}
                onChange={handleChange}
                label="Town/City"
                placeholder="Town/City"
                disabled={isAddressLocked}
              />

              <FormInput
                name="county"
                autocomplete="address-level1"
                value={values?.county}
                errorMsg={touched.county && errors?.county}
                valid={isValidField(values, touched, errors, "county")}
                onBlur={handleBlur}
                onChange={handleChange}
                label="County"
                placeholder="County"
                disabled={isAddressLocked}
              />
            </>
          ) : (
            <>
              <FormInput
                name="address1"
                autocomplete="address-line1"
                value={values?.address1}
                errorMsg={touched.address1 && errors?.address1}
                valid={isValidField(values, touched, errors, "address1")}
                onBlur={handleBlur}
                onChange={handleChange}
                label="First line of address (including house number)"
                placeholder="First line of address (including house number)"
                disabled={isAddressLocked}
              />
              <FormInput
                name="address2"
                autocomplete="address-line2"
                value={values?.address2}
                errorMsg={touched.address2 && errors?.address2}
                valid={isValidField(values, touched, errors, "address2")}
                onBlur={handleBlur}
                onChange={handleChange}
                label="Second line of address"
                placeholder="Second line of address"
                disabled={isAddressLocked}
              />
              <FormInput
                name="address3"
                autocomplete="address-level2"
                value={values?.address3}
                errorMsg={touched.address3 && errors?.address3}
                valid={isValidField(values, touched, errors, "address3")}
                onBlur={handleBlur}
                onChange={handleChange}
                label="Town/city"
                placeholder="Town/city"
                disabled={isAddressLocked}
              />
              <FormInput
                name="address4"
                autocomplete="address-level1"
                value={values?.address4}
                errorMsg={touched.address4 && errors?.address4}
                valid={isValidField(values, touched, errors, "address4")}
                onBlur={handleBlur}
                onChange={handleChange}
                label="County"
                placeholder="County"
                disabled={isAddressLocked}
              />
            </>
          )}
        </>
      )}
      {isManualAddress && isAddressLocked ? (
        <div className="mt-8 flex flex-col md:flex-row gap-3 md:w-full w-full justify-center">
          <ButtonRound
            text="Search address"
            type="success"
            className="md:w-1/2 w-full"
            onClick={(e) => {
              e.preventDefault();
              setIsAddressLocked(false);
              setFieldValue("isManualAddress", false);
            }}
          />
          <ButtonRound
            text="Edit address"
            type="primary"
            className="md:w-1/2 w-full"
            onClick={(e) => {
              e.preventDefault();
              setIsAddressLocked(false);
            }}
          />
        </div>
      ) : (
        <ButtonRound
          text={isManualAddress ? "Search address" : "Can't find the address?"}
          type="success"
          className="mt-8 md:w-1/2 w-full mx-auto"
          onClick={(e) => {
            e.preventDefault();
            setFieldValue("isManualAddress", !isManualAddress);
            if (isManualAddress) setIsAddressLocked(false);
          }}
        />
      )}
    </>
  );
};
